// The clipboard text behind the Super Admin "Copy reset link" and "Reset to
// default & copy message" actions.
//
// One module, used by every screen that offers these actions, so the wording
// cannot drift between Super Admin -> User Management, Team Members -> Managed
// Users and Team Members -> All.
//
// Straight quotes only. Curly quotes survive a code review and then arrive
// mangled in WhatsApp, which is where most of these messages actually go.

// Must equal settings.SUPERADMIN_DEFAULT_TEMPORARY_PASSWORD on the backend.
// The API deliberately never echoes the credential (v6 security contract), so
// this constant is the frontend's half of that agreement. Pinned by
// dentalcrm/tests/management/test_super_admin_reset_modes.py.
export const DEFAULT_TEMPORARY_PASSWORD = 'Welcome123!!';

export const CRM_LOGIN_URL = 'https://dental-sem.co.uk/';

/**
 * The message for "Reset to default & copy message".
 *
 * The four steps are the live UI as of 2026-09-24: AccountPopover has exactly
 * one menu entry (Profile), UserProfile renders a 'Password' tab, and
 * ProfilePassword asks only for the new password twice -- no current password.
 * If any of those change, change this too.
 */
export function buildCredentialMessage({ email, password = DEFAULT_TEMPORARY_PASSWORD }) {
    return [
        'DentalSEM CRM login details.',
        `    URL: ${CRM_LOGIN_URL}`,
        `    Email: ${email}`,
        `    Password: ${password}`,
        '    How to change your password:',
        '    1. Click on your profile image at the top right corner of the navigation bar.',
        "    2. Choose 'Profile' from the dropdown menu.",
        "    3. Select 'Password'.",
        '    4. Enter your new password.',
    ].join('\n');
}

/**
 * The message for "Copy reset password link".
 *
 * A naked URL reads like spam and says nothing about expiry, so it travels
 * with two lines of context. `resetUrl` is whatever the API returned -- never
 * rebuilt here, because the frontend guessing at the link shape is how the
 * September drift bug happened on the backend.
 */
export function buildResetLinkMessage({ resetUrl }) {
    return [
        'DentalSEM CRM password reset.',
        '    Click the link below to set your password. It expires in 3 days.',
        `    ${resetUrl}`,
    ].join('\n');
}

/**
 * Turn a failed reset response into something a human can act on.
 *
 * `status === 0` is apiCall.js's shape for "axios never got a response": a CORS
 * preflight the browser refused to follow, a DNS/TLS failure, or a timeout.
 * That case has no body, so the old `response.data?.detail || 'Failed to reset
 * password'` collapsed it into the same sentence a real backend refusal
 * produces -- and the backend, never having seen the request, has no log line
 * or Sentry event to correct the impression.
 *
 * That is exactly how 2026-09-28 went: `Idempotency-Key` was missing from the
 * backend's CORS_ALLOW_HEADERS, so only `temporary_credential` (the one mode
 * that sends a custom header) was blocked, and the screen said nothing more
 * useful than "Failed to reset password". Naming the shape of the failure is
 * the difference between reading one setting and reading the whole feature.
 */
export function resetFailureMessage(response) {
    const detail = response?.data?.detail;
    if (detail) return Array.isArray(detail) ? detail.join(' ') : detail;
    if (response && response.status === 0) {
        return 'The reset never reached the server — the request was blocked before it was sent. This is a configuration problem, not a permissions one; please report it.';
    }
    return 'Failed to reset password';
}

/**
 * Copy text to the clipboard, falling back to a hidden textarea.
 *
 * navigator.clipboard is unavailable on insecure origins and in some embedded
 * webviews; a reset flow that silently fails to copy is worse than useless, so
 * the caller always learns whether it worked.
 */
export async function copyToClipboard(text) {
    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (error) {
        // fall through to the legacy path
    }
    try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand('copy');
        document.body.removeChild(textarea);
        return copied;
    } catch (error) {
        return false;
    }
}
